import React, { useEffect, useRef } from 'react';
import Reveal from './Reveal';

/**
 * VideoBanner
 * - Square video card (1:1) with layered green tint & glow
 * - Optional side content: eyebrow, title, description, children
 * - `reverse` puts the video on the right; default: left
 */
export default function VideoBanner({
  webm,
  mp4,
  eyebrow,
  title,
  description,
  children,
  label,
  reverse = false,
}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const tryPlay = () => {
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    el.addEventListener('canplay', tryPlay, { once: true });
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && tryPlay()),
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => {
      el.removeEventListener('canplay', tryPlay);
      io.disconnect();
    };
  }, []);

  const videoCol = (
    <Reveal>
      <div className="relative aspect-square w-full rounded-3xl overflow-hidden border border-blue-500/12 bg-[#ffffff]">
        <video
          ref={ref}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          defaultMuted
          playsInline
          webkit-playsinline="true"
          preload="auto"
          disableRemotePlayback
          aria-hidden="true"
        >
          <source src={webm} type="video/webm" />
          <source src={mp4} type="video/mp4" />
        </video>
        {/* Minimal overlay — video stays the star */}
        {/* Bottom fade only where label sits, so dark label text is readable */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#ffffff] via-[#ffffff]/70 to-transparent" />
        {/* Soft blue color accent */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(37,99,235,0.12),transparent_55%)]" />
        <div className="absolute inset-0 mix-blend-overlay bg-sky-500/8" />
        {/* Corner marks — with subtle white pill for readability on any bg */}
        <div className="absolute top-4 left-4 rounded-full bg-white/85 backdrop-blur px-2 py-0.5 font-mono text-[10px] tracking-widest text-blue-600 shadow-sm">▍ LIVE</div>
        <div className="absolute top-4 right-4 rounded-full bg-white/85 backdrop-blur px-2 py-0.5 flex items-center gap-1.5 text-[10px] tracking-widest text-blue-600 shadow-sm">
          LEADEVEREST
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(37,99,235,0.9)]" />
        </div>
        {/* Bottom label */}
        {label && (
          <div className="absolute inset-x-5 bottom-5 flex items-end justify-between">
            <div className="font-display text-xl md:text-2xl font-medium text-slate-900 leading-tight max-w-[80%]">
              {label}
            </div>
            <div className="font-mono text-[10px] tracking-widest text-blue-600/80">01</div>
          </div>
        )}
        {/* Ambient glow behind */}
        <div className="absolute -inset-8 -z-10 blur-3xl bg-blue-500/10 rounded-full" />
      </div>
    </Reveal>
  );

  const textCol = (
    <div className="space-y-5">
      {eyebrow && (
        <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/25 bg-blue-500/[0.04] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-blue-600">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(37,99,235,0.9)]" />
          {eyebrow}
        </div>
      )}
      {title && (
        <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 leading-[1.05]">
          {title}
        </h3>
      )}
      {description && (
        <p className="text-slate-700/65 leading-relaxed text-[15px] md:text-base max-w-xl">
          {description}
        </p>
      )}
      {children}
    </div>
  );

  return (
    <section className="relative py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className={`lg:col-span-6 ${reverse ? 'lg:order-2' : ''}`}>{videoCol}</div>
          <div className={`lg:col-span-6 ${reverse ? 'lg:order-1 lg:pr-8' : 'lg:pl-8'}`}>{textCol}</div>
        </div>
      </div>
    </section>
  );
}
